import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {withTranslation} from 'react-i18next';
import DatatableLoadMore from '../../../common/components/Datatable/DatatableLoadMore';
import gql from 'graphql-tag';
import {dateFormatFR} from '../../../utils';
import {BILLING_SUBSCRIPTION_STATUS} from '../../../common/constants';
import styled from 'styled-components';
import {Col, Row} from 'react-bootstrap';
import {flowRight as compose} from 'lodash';
import RowActionInvoicesAdmin from './RowActionInvoicesAdmin';
import SelectSubscriptionPlan from './SelectSubscriptionPlan';
import DisplayBillingSubscriptionStatus from './DisplayBillingSubscriptionStatus';
import FlexRow from '../../../common/components/layout/FlexRow';
import {formatAmount} from '../../../common/components/DisplayAmount';
import SelectSubscriptionBillingStatus from './SelectSubscriptionBillingStatus';
import DisplayBillingPlan from './DisplayBillingPlan';
import DisplayCollectionMethod from './DisplayCollectionMethod';
import SelectCollectionMethod from './SelectCollectionMethod';

const MargedDiv = styled.div`
  margin: 5px;
`;

const MargedLeftDiv = styled.div`
  margin-left: 8px;
  font-size: 13px;;
`;


const GRAPHQL_QUERY_BILLING_SUBSCRIPTIONS = gql`
  query billingSubscriptions($criteria: CriteriaListInput, $additionalCriteria: BillingSubscriptionsCriteriaListInput) {
    billingSubscriptions(criteria: $criteria, additionalCriteria: $additionalCriteria) {
      rows {
        id
        billingCycleAnchor
        cancelAt
        cancelAtPeriodEnd
        canceledAt
        collectionMethod
        created
        currentPeriodEnd
        currentPeriodStart
        user{
          id
          firstName
          lastName
          merchant{
            id
            businessName
          }
        }
        daysUntilDue
        endedAt
        latestInvoice{
          id
          invoicePdf
          adminStripeUrl
          status
        }
        plan{
          id
          amount
          currency
          nickname
          forRole
          interval
        }
        quantity
        startDate
        status
        trialEnd
        trialStart
      }
      hasMore
      id
    }
  }
`;

const displayCurrentPeriod = (row, t) => {
  if (!row.currentPeriodStart || !row.currentPeriodEnd) {
    return t('noCurrentSubscription');
  } else {
    return t('from') + ' ' + dateFormatFR({date:row.currentPeriodStart}) + ' ' + t('to') + ' ' + dateFormatFR({date:row.currentPeriodEnd});
  }
};

const displayUser = user => {
  if (!user) {
    return '';
  }
  if (user.merchant) {
    return user.merchant.businessName;
  } else {
    return user.firstName + ' ' + user.lastName;
  }
};

const displayStatus = (row, t) => {
  const status = row.status;
  if (status === BILLING_SUBSCRIPTION_STATUS.TRIALING && row.trialEnd) {
    return (
      <FlexRow>
        <DisplayBillingSubscriptionStatus status={status} />
        <MargedLeftDiv>{t('until') + ' ' + dateFormatFR({date:row.trialEnd})}</MargedLeftDiv>
      </FlexRow>
    );
  } else {
    return <DisplayBillingSubscriptionStatus status={status} />;
  }
};

const displaySubscriptionContent = (row, t) => {
  return (
    <FlexRow>
      <div>{t('plan', {count: row.quantity}) + ' ' + t('at') + ' ' + formatAmount(row.plan.amount)}</div>
    </FlexRow>
  );
};


const columns = t => [
  {
    Header: t('date'),
    accessor: 'created',
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
  },
  {
    Header: t('subscriptionStatus.title'),
    accessor: row => row,
    Cell: ({cell}) => displayStatus(cell.value, t),
  },
  {
    Header: t('content'),
    accessor: row => row,
    Cell: ({cell}) => displaySubscriptionContent(cell.value, t),
  },
  {
    Header: t('collectionMethod'),
    accessor: 'collectionMethod',
    Cell: ({cell}) => <DisplayCollectionMethod collectionMethod={cell.value}/>,
  },
  {
    Header: t('currentPeriod'),
    accessor: row => row,
    Cell: ({cell}) => displayCurrentPeriod(cell.value, t),
  },
  {
    Header: t('userOrMerchant'),
    accessor: 'user',
    Cell: ({cell}) => displayUser(cell.value),
  },
  {
    Header: t('plan'),
    accessor: 'plan',
    Cell: ({cell}) => <DisplayBillingPlan plan={cell.value}/>,
  },
  {
    Header: t('latestInvoice'),
    accessor: 'latestInvoice',
    Cell: ({cell}) => <RowActionInvoicesAdmin invoice={cell.value}/>,
  },
];

const createAdditionalCriteria = (plan, status, collectionMethod) => {
  let filters = {};
  if (plan !== 'default' && plan !== 'all') {
    filters = {
      plan,
      ...filters,
    };
  }

  if (status !== 'default' && status !== 'all') {
    filters = {
      status,
      ...filters,
    };
  }

  if (collectionMethod !== 'default' && collectionMethod !== 'all') {
    filters = {
      collectionMethod,
      ...filters,
    };
  }

  return filters;
};

const SubscriptionsAdmin = ({t}) => {
  const [planFilter, setPlanFilter] = useState('default');
  const [statusFilter, setStatusFilter] = useState('default');
  const [collectionMethodFilter, setCollectionMethodFilter] = useState('default');

  const additionalFilters = (
    <FlexRow>
      <MargedDiv>
        <SelectSubscriptionBillingStatus value={statusFilter} allowNull onChange={e => setStatusFilter(e.target.value)}/>
      </MargedDiv>
      <MargedDiv>
        <SelectSubscriptionPlan value={planFilter} allowNull onChange={e => setPlanFilter(e.target.value)}/>
      </MargedDiv>
      <MargedDiv>
        <SelectCollectionMethod value={collectionMethodFilter} allowNull onChange={e => setCollectionMethodFilter(e.target.value)}/>
      </MargedDiv>
    </FlexRow>
  );

  return (
    <div className="general-container">
      <Row>
        <Col xs={12}>
          <DatatableLoadMore
            query={GRAPHQL_QUERY_BILLING_SUBSCRIPTIONS}
            keyResult="billingSubscriptions"
            columns={columns(t)}
            additionalCriteria={createAdditionalCriteria(planFilter, statusFilter, collectionMethodFilter)}
            additionalFilters={additionalFilters}
          />
        </Col>
      </Row>
    </div>
  );
};

SubscriptionsAdmin.propTypes = {
  t: PropTypes.func.isRequired,
};

export default compose(
  withTranslation('billing'),
)(SubscriptionsAdmin);
